import { useState, useEffect } from 'react';
import type { CustomerProfile } from '../api/portalApi';
import styles from './CssEditor.module.css';

const CSS_SELECTORS = [
  { selector: '.chikchak-widget', desc: 'Widget container' },
  { selector: '.chikchak-header', desc: 'Header bar' },
  { selector: '.chikchak-btn', desc: 'Buttons' },
  { selector: '.chikchak-input', desc: 'Text inputs' },
  { selector: '.chikchak-message', desc: 'Chat messages' },
  { selector: '.chikchak-badge', desc: 'Notification badges' },
];

interface CssEditorProps {
  customer: CustomerProfile;
  onSave: (css: string) => Promise<void>;
}

export function CssEditor({ customer, onSave }: CssEditorProps) {
  const [css, setCss] = useState(customer.customCss ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const hasChanges = css !== (customer.customCss ?? '');

  useEffect(() => {
    setCss(customer.customCss ?? '');
  }, [customer.customCss]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(css);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className={styles.title}>CSS Editor</h1>
      <p className={styles.subtitle}>
        Customize the appearance of your ChikChak plugin widgets. Your CSS is injected into the plugin on every page load.
      </p>

      <div className={styles.editorWrap}>
        <div className={styles.editorHeader}>
          <span className={styles.editorLabel}>Custom CSS</span>
          <div className={styles.editorActions}>
            {hasChanges && <span className={styles.unsaved}>Unsaved changes</span>}
            {saved && <span className={styles.savedBadge}>Saved</span>}
            <button className={styles.saveBtn} onClick={handleSave} disabled={!hasChanges || saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        <textarea
          className={styles.editor}
          value={css}
          onChange={(e) => setCss(e.target.value)}
          placeholder={`/* Example: customize the widget */\n.chikchak-widget {\n  font-family: 'Inter', sans-serif;\n  --primary-color: #4f46e5;\n}`}
          spellCheck={false}
        />
      </div>

      <div className={styles.reference}>
        <h2 className={styles.refTitle}>Selector Reference</h2>
        <div className={styles.refGrid}>
          {CSS_SELECTORS.map((s) => (
            <div key={s.selector} className={styles.refItem}>
              <code>{s.selector}</code>
              <span>{s.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
